import { ReactNode } from 'react'
import RefreshIcon from '@mui/icons-material/Refresh'
import { IconButton } from '@mui/material'
import {
  GridRowId,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  selectedGridRowsSelector,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid'
import { MoreMenu } from 'components/MoreMenu'
import { IGetFields } from 'screens/Shipments/helpers/getFields'
import GridToolbarCopyFields from './GridToolbarCopyFields'
import GridToolbarGroup, { IGridToolbarGroupProps } from './GridToolbarGroup'
import GridToolbarQuickFilters, { IGridToolbarQuickFiltersProps } from './GridToolbarQuickFilters'
import GridToolbarSendPrescriptionsShipments from './GridToolbarSendPrescriptionsShipments'

export interface IBulkUpdateProviderProps {
  selectedRows: Map<GridRowId, any>
  bulkUpdate: (ids: any[], params: { [key: string]: any }, resetSelectedRows: () => void) => void
  resetSelectedRows: () => void
  loading?: boolean
}

export type IToolbarProps = IGridToolbarQuickFiltersProps &
  IGridToolbarGroupProps & {
    toolbarAdornments?: ReactNode | ReactNode[]
    showCopyPrescriptionsActions?: boolean
    showResendPrescriptionsAction?: boolean
    getMainFields?: IGetFields
    BulkUpdateSelectedRows?: ({
      selectedRows,
      bulkUpdate,
      resetSelectedRows,
      loading
    }: IBulkUpdateProviderProps) => JSX.Element | null
    bulkUpdate?: (
      ids: any[],
      params: { [key: string]: any },
      resetSelectedRows: IBulkUpdateProviderProps['resetSelectedRows']
    ) => void
    bulkUpdateLoading?: boolean
    bulkUpdateVisible?: boolean
    BulkDeleteSelectedRows?: ({
      selectedRows,
      bulkDelete
    }: {
      selectedRows: Map<GridRowId, any>
      bulkDelete: (ids: any[], params: { [key: string]: any }) => void
    }) => JSX.Element | null
    bulkDelete?: (ids: any[], params: { [key: string]: any }) => void
    bulkDeleteVisible?: boolean
    refresh?: () => void
    additionalActions?: ReactNode | ReactNode[]
    noExport?: boolean
  }

function Toolbar(props: IToolbarProps) {
  const {
    quickFilters,
    groupField,
    setGroupField,
    availableGroupColumns,
    resetFilterModel,
    showResendPrescriptionsAction,
    getMainFields,
    BulkUpdateSelectedRows,
    bulkUpdate,
    bulkUpdateLoading,
    bulkUpdateVisible,
    BulkDeleteSelectedRows,
    bulkDelete,
    bulkDeleteVisible,
    refresh,
    toolbarAdornments,
    additionalActions,
    noExport
  } = props

  const apiRef = useGridApiContext()

  const selectedRows = useGridSelector(apiRef, selectedGridRowsSelector)
  const resetSelectedRows = () => {
    apiRef.current.selectRows([], false, true)
  }
  const showBulkUpdate = bulkUpdateVisible === undefined ? selectedRows.size > 1 : bulkUpdateVisible
  const showBulkDelete = bulkDeleteVisible === undefined ? selectedRows.size > 1 : bulkDeleteVisible

  return (
    <GridToolbarContainer>
      {/*// @ts-ignore */}
      <GridToolbarColumnsButton />
      {/*// @ts-ignore */}
      <GridToolbarFilterButton />
      <GridToolbarQuickFilters quickFilters={quickFilters} resetFilterModel={resetFilterModel} />
      {showResendPrescriptionsAction && <GridToolbarSendPrescriptionsShipments />}
      <GridToolbarGroup
        groupField={groupField}
        setGroupField={setGroupField}
        availableGroupColumns={availableGroupColumns}
      />
      {/*// @ts-ignore */}
      <GridToolbarDensitySelector />
      {!noExport && <GridToolbarExport printOptions={{ disableToolbarButton: true }} />}
      {selectedRows.size > 0 && getMainFields && (
        <GridToolbarCopyFields
          buildRow={getMainFields}
          selectedRows={selectedRows}
          title="Copy Main Fields"
        />
      )}
      {showBulkUpdate && BulkUpdateSelectedRows && bulkUpdate && (
        <BulkUpdateSelectedRows
          selectedRows={selectedRows}
          bulkUpdate={bulkUpdate}
          resetSelectedRows={resetSelectedRows}
          loading={bulkUpdateLoading}
        />
      )}
      {showBulkDelete && BulkDeleteSelectedRows && bulkDelete && (
        <BulkDeleteSelectedRows selectedRows={selectedRows} bulkDelete={bulkDelete} />
      )}
      {refresh && (
        <IconButton
          onClick={() => {
            refresh()
          }}
          aria-label="Refresh"
        >
          <RefreshIcon />
        </IconButton>
      )}
      {additionalActions && <MoreMenu>{additionalActions}</MoreMenu>}
      {toolbarAdornments}
    </GridToolbarContainer>
  )
}

export default Toolbar
